/**
 * Host-aware URL rule (custom-domain epic ENG-3416): published resident
 * content on an org with a canonical custom host uses that host with the org
 * slug DROPPED (the host identifies the org); otherwise the default public
 * host keeps the slug. Admin/preview URLs never use the custom host.
 *
 * Client-safe on purpose: takes the canonical hostname as an argument and
 * never imports the custom-domain map (which carries cross-tenant internal
 * fields that must not ship in the browser bundle) — pages resolve the
 * hostname server-side via resolveCanonicalHostname and thread it through
 * PageContext.
 */
export const buildPublicUrlPrefix = (organization: string, canonicalHostname?: string | null): string =>
  canonicalHostname
    ? `https://${canonicalHostname}`
    : `${globalThis?.process?.env?.NEXT_PUBLIC_HOST_URL}/${organization}`;

/** slug is the canonical [organization, locale, ...rest] path segments. */
export const buildPublicUrlFromSlug = (slug: string[], canonicalHostname?: string | null): string => {
  const [organization, ...rest] = slug;
  const prefix = buildPublicUrlPrefix(organization, canonicalHostname);
  return rest.length > 0 ? `${prefix}/${rest.join('/')}` : prefix;
};

/**
 * Relative variant for same-site links (feed cards, language switcher): on
 * the org's canonical custom host the org segment is dropped (the middleware
 * re-prepends it internally); without a custom host the slugged path is kept
 * byte-identical to today's links.
 */
export const buildPublicPathFromSlug = (slug: string[], canonicalHostname?: string | null): string => {
  const [organization, ...rest] = slug;
  if (canonicalHostname) {
    return `/${rest.join('/')}`;
  }
  return rest.length > 0 ? `/${organization}/${rest.join('/')}` : `/${organization}`;
};
